import React from 'react';
import { observer } from 'mobx-react-lite';
import { FormControl, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material';

export interface Option<T = string | number> {
  value: T;
  label: string;
}

interface CommonDropdownProps<T = string | number> {
  options: Option<T>[];
  value: T | null;
  onChange: (value: T | null) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  className?: string;
  disabled?: boolean;
  /** Tam genişlik yerine içeriğe göre genişlesin. Varsayılan: false (mevcut fullWidth davranışı) */
  autoWidth?: boolean;
  /** autoWidth true iken minimum genişlik (karakter bazlı, varsayılan: 12) */
  minWidthChars?: number;
  /** autoWidth true iken maksimum genişlik (karakter bazlı, varsayılan: 35) */
  maxWidthChars?: number;
}

const CommonDropdown = observer(
  <T extends string | number>({
    options,
    value,
    onChange,
    label,
    placeholder,
    error,
    className,
    disabled,
    autoWidth = false,
    minWidthChars = 12,
    maxWidthChars = 35,
  }: CommonDropdownProps<T>) => {
    const selectId =
      label || placeholder
        ? `${(label || placeholder)!.replace(/\s+/g, '-').toLowerCase()}-select`
        : undefined;

    const muiValue = value === null || value === undefined ? '' : value.toString();

    const widthSx = autoWidth
      ? {
          minWidth: `${minWidthChars}ch`,
          width: 'fit-content',
          maxWidth: `${maxWidthChars}ch`,
        }
      : undefined;

    const baseSx: Record<string, object> = {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#e2e8f0',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#cbd5e1',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
        {
          borderColor: '#94a3b8',
          borderWidth: '1px',
          boxShadow: 'none',
        },
      '& .MuiInputLabel-root': {
        color: '#64748b',
      },
      '& .MuiInputLabel-root.Mui-focused': {
        color: '#64748b',
      },
      '& .MuiSelect-select': {
        color: '#1e293b',
        fontSize: '0.875rem',
        fontWeight: 500,
        paddingTop: '8px',
        paddingBottom: '8px',
      },
      '& .MuiOutlinedInput-root': {
        backgroundColor: '#fff',
        borderRadius: '8px',
        '&:hover': {
          backgroundColor: '#fff',
        },
        '&.Mui-focused': {
          boxShadow: 'none',
        },
      },
    };

    const selectSelectSx = autoWidth
      ? { overflow: 'hidden', textOverflow: 'ellipsis' }
      : {};
    const mergedSx = {
      ...baseSx,
      ...(widthSx || {}),
      '& .MuiSelect-select': { ...baseSx['& .MuiSelect-select'], ...selectSelectSx },
    };

    return (
      <FormControl
        fullWidth={!autoWidth}
        error={!!error}
        className={className}
        disabled={disabled}
        variant="outlined"
        size="small"
        sx={mergedSx}
      >
        {(label || placeholder) && (
          <InputLabel id={`${selectId}-label`}>{label || placeholder}</InputLabel>
        )}
        <Select
          labelId={`${selectId}-label`}
          id={selectId}
          value={muiValue}
          onChange={e => {
            const newValue = e.target.value;
            if (newValue === '') {
              onChange(null);
            } else {
              const typedValue =
                typeof options[0]?.value === 'number' ? Number(newValue) : newValue;
              onChange(typedValue as T);
            }
          }}
          label={label || placeholder}
        >
          {options.map(option => (
            <MenuItem key={option.value?.toString()} value={option.value?.toString()}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>
    );
  }
);

export default CommonDropdown;
